import React, { useEffect, useState, useCallback } from "react";
import { Mail, MessageCircle, Send, Inbox as InboxIcon, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

export default function AdminInbox() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [channel, setChannel] = useState("email");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/inbox");
    setItems(r.data);
    setActive((prev) => (prev ? r.data.find((i) => i.id === prev.id) || r.data[0] : r.data[0]) || null);
  }, []);

  useEffect(() => { load(); }, [load]);

  const reply = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/inbox/${active.id}/reply`, { channel, body });
      if (channel === "whatsapp") {
        if (data.delivery?.sent) toast.success("WhatsApp message sent");
        else toast.error(`WhatsApp not sent: ${data.delivery?.info || "no phone / not joined sandbox"}`);
      } else {
        toast.success("Email reply logged (sending is mocked)");
      }
      setBody("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl">
      <p className="text-coral text-xs font-bold uppercase tracking-widest mb-2">Admin Portal</p>
      <h1 className="font-serif text-4xl font-bold tracking-tight">Unified Inbox</h1>
      <p className="text-zinc-500 mt-2 mb-6">Website inquiries, WhatsApp messages, and emails in one place. <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-semibold">WhatsApp is LIVE (Twilio sandbox)</span> <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-semibold">Email sending is MOCKED</span></p>

      <div className="grid lg:grid-cols-3 gap-6 h-[640px]">
        {/* List */}
        <div className="bg-white border border-zinc-200 rounded-sm overflow-y-auto" data-testid="inbox-list">
          {items.length === 0 ? (
            <div className="p-10 text-center text-zinc-400">
              <InboxIcon className="mx-auto mb-3 text-zinc-300" size={36} strokeWidth={1.2} />
              No messages yet.
            </div>
          ) : items.map((it) => (
            <button key={it.id} onClick={() => setActive(it)} className={`w-full text-left p-4 border-b border-zinc-100 transition ${active?.id === it.id ? "bg-zinc-50 border-l-2 border-l-coral" : "hover:bg-zinc-50"}`} data-testid={`inbox-item-${it.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold text-sm truncate">{it.name}</span>
                <StatusBadge status={it.status} />
              </div>
              <div className="text-xs text-zinc-500 truncate">{it.email}</div>
              <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{it.message}</div>
            </button>
          ))}
        </div>

        {/* Thread */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-zinc-400">Select a message</div>
          ) : (
            <>
              <div className="p-5 border-b border-zinc-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-serif text-lg font-bold">{active.name}</h3>
                    <div className="text-sm text-zinc-500 flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1"><Mail size={13} /> {active.email}</span>
                      {active.company && <span className="flex items-center gap-1"><Building2 size={13} /> {active.company}</span>}
                    </div>
                  </div>
                  <StatusBadge status={active.status} />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-4" data-testid="inbox-original">
                  <div className="text-xs text-zinc-400 mb-1">Original inquiry · {new Date(active.created_at).toLocaleString()}</div>
                  <p className="text-sm text-zinc-700 whitespace-pre-wrap">{active.message}</p>
                </div>
                {(active.replies || []).map((rep, i) => (
                  rep.direction === "in" ? (
                    <div key={i} className="bg-zinc-50 border border-zinc-200 rounded-sm p-4 mr-8" data-testid={`inbox-inbound-${i}`}>
                      <div className="text-xs text-zinc-500 font-semibold flex items-center gap-1.5 mb-1">
                        <MessageCircle size={13} /> {rep.by} · via WhatsApp
                      </div>
                      <p className="text-sm text-zinc-700 whitespace-pre-wrap">{rep.body}</p>
                    </div>
                  ) : (
                    <div key={i} className="bg-coral/5 border border-coral/20 rounded-sm p-4 ml-8" data-testid={`inbox-reply-${i}`}>
                      <div className="text-xs text-coral font-semibold flex items-center gap-1.5 mb-1">
                        {rep.channel === "whatsapp" ? <MessageCircle size={13} /> : <Mail size={13} />}
                        Replied via {rep.channel} · {rep.by}{rep.delivery === "failed" ? " · not delivered" : ""}
                      </div>
                      <p className="text-sm text-zinc-700 whitespace-pre-wrap">{rep.body}</p>
                    </div>
                  )
                ))}
              </div>
              <form onSubmit={reply} className="p-4 border-t border-zinc-200" data-testid="reply-form">
                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={() => setChannel("email")} className={`px-3 py-1.5 rounded-sm text-xs font-semibold flex items-center gap-1.5 transition ${channel === "email" ? "bg-ink text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid="channel-email">
                    <Mail size={13} /> Email
                  </button>
                  <button type="button" onClick={() => setChannel("whatsapp")} className={`px-3 py-1.5 rounded-sm text-xs font-semibold flex items-center gap-1.5 transition ${channel === "whatsapp" ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-600"}`} data-testid="channel-whatsapp">
                    <MessageCircle size={13} /> WhatsApp
                  </button>
                </div>
                <div className="flex gap-2">
                  <input value={body} onChange={(e) => setBody(e.target.value)} placeholder={`Reply via ${channel}...`} className="flex-1 bg-white border border-zinc-300 rounded-sm px-4 py-2.5 text-sm focus:border-coral focus:ring-1 focus:ring-coral outline-none" data-testid="reply-input" />
                  <button disabled={sending} className="bg-coral hover:bg-coral-hover text-white font-semibold px-4 rounded-sm transition disabled:opacity-60 flex items-center gap-1.5" data-testid="send-reply">
                    <Send size={15} /> Send
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
